<template>
  <v-container style="margin-top: 70px; margin-bottom: 70px;" fluid class="operation-settings pa-0">
    <v-row class="justify-center">
      <v-col cols="12" lg="10">
        <v-card class="settings-card">
          <v-form @submit.prevent="submitHandler" ref="form">
            <!-- Header -->
            <div class="settings-header">
              <div class="d-flex align-center">
                <v-btn @click="openMode = false" icon class="back-button ml-4">
                  <v-icon>mdi-arrow-right</v-icon>
                </v-btn>
                <h1 class="text-h4 font-weight-bold primary-text mb-0">הגדרות פעולה</h1>
              </div>
            </div>

            <v-divider></v-divider>

            <!-- Content -->
            <v-container class="py-6">
              <v-row>
                <!-- Basic Info -->
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedOperation.name" outlined label="שם הפעולה" maxlength="32"
                    class="custom-input" :rules="[v => !!v || 'שדה חובה']" required></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedOperation.action_name" outlined
                    label="שם פעולה למשתנים (אנגלית בלבד או מספרים)" class="custom-input" :rules="[
                      v => !!v || 'שדה חובה',
                      v => /^[a-zA-Z0-9]+$/.test(v) || 'השתמש רק באותיות באנגלית או מספרים'
                    ]"></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-text-field v-model="editedOperation.description" outlined label="תאור הפעולה"
                    class="custom-input"></v-text-field>
                </v-col>

                <!-- Method & Condition -->
                <v-col cols="12" sm="6">
                  <v-select :items="requestMethod" v-model="editedOperation.action.method" outlined label="שיטת שליחה"
                    class="custom-input"></v-select>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="condition-card">
                    <div class="condition-header">
                      <v-icon color="#0d2c6d" small class="mr-2">mdi-code-brackets</v-icon>
                      <span class="text-subtitle-2 font-weight-medium">תנאי הפעלה</span>
                      <v-menu v-model="showConditionHelp" :close-on-content-click="false" :nudge-width="600" offset-y
                        bottom left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon small class="help-icon mr-2" @click="showConditionHelp = !showConditionHelp">
                            <v-icon small color="#0d2c6d">mdi-help-circle-outline</v-icon>
                          </v-btn>
                        </template>
                        <div class="help-tooltip">
                          <div class="help-title">
                            איך לכתוב תנאים
                            <v-btn icon x-small class="close-help" @click="showConditionHelp = false">
                              <v-icon small>mdi-close</v-icon>
                            </v-btn>
                          </div>
                          <div class="help-content">
                            <div class="help-section">
                              <div class="help-subtitle">אופרטורי השוואה:</div>
                              <div class="help-description">שווה (זהה לחלוטין)</div>
                              <div class="help-example">===</div>
                              <div class="help-description">לא שווה</div>
                              <div class="help-example">!==</div>
                              <div class="help-description">קטן מ, גדול מ, קטן או שווה, גדול או שווה</div>
                              <div class="help-example">
                                <>
                                  <=>=
                              </div>
                            </div>
                            <div class="help-section">
                              <div class="help-subtitle">אופרטורים לוגיים:</div>
                              <div class="help-description">AND - שני התנאים חייבים להיות נכונים</div>
                              <div class="help-example">&&</div>
                              <div class="help-description">OR - אחד מהתנאים חייב להיות נכון</div>
                              <div class="help-example">||</div>
                            </div>
                            <div class="help-section">
                              <div class="help-subtitle">דוגמאות לתנאים:</div>
                              <div class="help-description">סטטוס שווה ל-active</div>
                              <div class="help-example">${status} === active</div>
                              <div class="help-description">גיל 18 ומעלה וסטטוס מאומת</div>
                              <div class="help-example">${age} >= 18 && ${status} === verified</div>
                              <div class="help-description">סוג פרימיום או ציון מעל 90</div>
                              <div class="help-example">${type} === premium || ${score} > 90</div>
                            </div>
                            <div class="help-section">
                              <div class="help-subtitle">שימוש במשתנים:</div>
                              <div class="help-description">משתנה מהפריט</div>
                              <div class="help-example">${field_name}</div>
                              <div class="help-description">משתנה מ-webhook קודם</div>
                              <div class="help-example">${Webhook_action_name_status}</div>
                              <div class="help-description">משתנה קבוע</div>
                              <div class="help-example">${timestamp}</div>
                            </div>
                          </div>
                        </div>
                      </v-menu>
                    </div>
                    <div class="condition-content">
                      <v-text-field v-model="editedOperation.action.condition" hide-details dense
                        label="תנאי (לדוגמה: ${status} === active && ${age} >= 18)" class="condition-string-input"
                        style="direction: ltr;"></v-text-field>
                    </div>
                  </div>
                </v-col>

                <!-- Trigger Field & URL -->
                <v-col cols="3" v-if="editedOperation.triger === 'when_row_updated'">
                  <v-select v-model="editedOperation.action.trigerField" :items="fields" item-text="hebrew_name"
                    item-value="name" outlined label="שדה להפעלה" class="custom-input" :rules="[v => !!v || 'שדה חובה']"
                    required></v-select>
                </v-col>

                <!-- URL with Help Icon -->
                <v-col :cols="editedOperation.triger === 'when_row_updated' ? 9 : 12">
                  <div class="d-flex align-center mb-2">
                    <span class="text-subtitle-1 font-weight-medium ml-2">URL</span>
                    <v-menu v-model="showUrlHelp" :close-on-content-click="false" :nudge-width="500" offset-y bottom
                      left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small class="help-icon" @click="showUrlHelp = !showUrlHelp">
                          <v-icon small color="#0d2c6d">mdi-help-circle-outline</v-icon>
                        </v-btn>
                      </template>
                      <div class="help-tooltip">
                        <div class="help-title">
                          איך לשלוח משתנים ב-API
                          <v-btn icon x-small class="close-help" @click="showUrlHelp = false">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                        <div class="help-content">
                          <div class="help-section">
                            <div class="help-subtitle">משתנים מהפריט:</div>
                            <div class="help-example">${AA_Name}</div>
                            <div class="help-description">בתוך הסוגריים מגדירים את שם השדה</div>
                          </div>
                          <div class="help-section">
                            <div class="help-subtitle">משתנה מ-webhook קודם:</div>
                            <div class="help-example">Webhook_action_name_status</div>
                            <div class="help-description">שם הפעולה + שם המשתנה</div>
                          </div>
                          <div class="help-section">
                            <div class="help-subtitle">משתנים קבועים זמינים:</div>
                            <div class="help-variables">
                              <div class="help-variable">${timestamp} - זמן נוכחי (ISO)</div>
                              <div class="help-variable">${timestamp_IL} - זמן מקומי (ישראל)</div>
                              <div class="help-variable">${date} - תאריך (DD-MM-YYYY)</div>
                              <div class="help-variable">${time} - שעה (HH:MM:SS)</div>
                              <div class="help-variable">${year} - שנה</div>
                              <div class="help-variable">${month} - חודש</div>
                              <div class="help-variable">${day} - יום</div>
                              <div class="help-variable">${weekday} - יום בשבוע (עברית)</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-menu>
                  </div>
                  <v-text-field v-model="editedOperation.action.url" outlined label="URL" class="custom-input"
                    :rules="[v => !!v || 'שדה חובה']" required style="direction: ltr;"></v-text-field>
                </v-col>

                <!-- Headers with Help Icon -->
                <v-col cols="12">
                  <div class="d-flex align-center mb-2">
                    <span class="text-subtitle-1 font-weight-medium ml-2">Headers</span>
                    <v-menu v-model="showHeadersHelp" :close-on-content-click="false" :nudge-width="500" offset-y bottom
                      left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small class="help-icon" @click="showHeadersHelp = !showHeadersHelp">
                          <v-icon small color="#0d2c6d">mdi-help-circle-outline</v-icon>
                        </v-btn>
                      </template>
                      <div class="help-tooltip">
                        <div class="help-title">
                          איך לשלוח משתנים ב-API
                          <v-btn icon x-small class="close-help" @click="showHeadersHelp = false">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                        <div class="help-content">
                          <div class="help-section">
                            <div class="help-subtitle">משתנים מהפריט:</div>
                            <div class="help-example">${AA_Name}</div>
                            <div class="help-description">בתוך הסוגריים מגדירים את שם השדה</div>
                          </div>
                          <div class="help-section">
                            <div class="help-subtitle">משתנה מ-webhook קודם:</div>
                            <div class="help-example">Webhook_action_name_status</div>
                            <div class="help-description">שם הפעולה + שם המשתנה</div>
                          </div>
                          <div class="help-section">
                            <div class="help-subtitle">משתנים קבועים זמינים:</div>
                            <div class="help-variables">
                              <div class="help-variable">${timestamp} - זמן נוכחי (ISO)</div>
                              <div class="help-variable">${timestamp_IL} - זמן מקומי (ישראל)</div>
                              <div class="help-variable">${date} - תאריך (DD-MM-YYYY)</div>
                              <div class="help-variable">${time} - שעה (HH:MM:SS)</div>
                              <div class="help-variable">${year} - שנה</div>
                              <div class="help-variable">${month} - חודש</div>
                              <div class="help-variable">${day} - יום</div>
                              <div class="help-variable">${weekday} - יום בשבוע (עברית)</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-menu>
                  </div>
                  <v-textarea v-model="editedOperation.action.headers" outlined label="Headers" rows="3"
                    class="custom-input" style="direction: ltr;"></v-textarea>
                </v-col>

                <!-- Body with Help Icon -->
                <v-col cols="12">
                  <div class="d-flex align-center mb-2">
                    <span class="text-subtitle-1 font-weight-medium ml-2">Body</span>
                    <v-menu v-model="showBodyHelp" :close-on-content-click="false" :nudge-width="500" offset-y bottom
                      left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small class="help-icon" @click="showBodyHelp = !showBodyHelp">
                          <v-icon small color="#0d2c6d">mdi-help-circle-outline</v-icon>
                        </v-btn>
                      </template>
                      <div class="help-tooltip">
                        <div class="help-title">
                          איך לשלוח משתנים ב-API
                          <v-btn icon x-small class="close-help" @click="showBodyHelp = false">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                        <div class="help-content">
                          <div class="help-section">
                            <div class="help-subtitle">משתנים מהפריט:</div>
                            <div class="help-example">${AA_Name}</div>
                            <div class="help-description">בתוך הסוגריים מגדירים את שם השדה</div>
                          </div>
                          <div class="help-section">
                            <div class="help-subtitle">משתנה מ-webhook קודם:</div>
                            <div class="help-example">Webhook_action_name_status</div>
                            <div class="help-description">שם הפעולה + שם המשתנה</div>
                          </div>
                          <div class="help-section">
                            <div class="help-subtitle">משתנים קבועים זמינים:</div>
                            <div class="help-variables">
                              <div class="help-variable">${timestamp} - זמן נוכחי (ISO)</div>
                              <div class="help-variable">${timestamp_IL} - זמן מקומי (ישראל)</div>
                              <div class="help-variable">${date} - תאריך (DD-MM-YYYY)</div>
                              <div class="help-variable">${time} - שעה (HH:MM:SS)</div>
                              <div class="help-variable">${year} - שנה</div>
                              <div class="help-variable">${month} - חודש</div>
                              <div class="help-variable">${day} - יום</div>
                              <div class="help-variable">${weekday} - יום בשבוע (עברית)</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-menu>
                  </div>
                  <v-textarea v-model="editedOperation.action.body" :rules="[jsonRule]" outlined label="Body" rows="5"
                    class="custom-input" style="direction: ltr;"></v-textarea>
                </v-col>

                <!-- Switches -->
                <v-col cols="12">
                  <div class="switches-container">
                    <div class="switch-item d-flex align-center">
                      <v-switch v-model="socketEnabled" :color="$store.state.colorPrimary" hide-details class="ma-0"
                        inset label="הפעל בדיקה"></v-switch>
                      <v-icon v-if="socketEnabled" :color="$store.state.colorPrimary" small class="mr-4 view-icon"
                        @click.stop="socketDataDialog = true">
                        mdi-eye-outline
                      </v-icon>
                    </div>
                    <div class="switch-item">
                      <v-switch v-model="editedOperation.action.send_reports" :color="$store.state.colorPrimary"
                        hide-details inset label="שלח שגיאה למייל"></v-switch>
                    </div>

                    <div class="switch-item">
                      <v-switch v-model="editedOperation.action.mode" :color="$store.state.colorPrimary" hide-details
                        inset label="מופעל/מושבת"></v-switch>
                    </div>
                  </div>
                </v-col>

                <!-- Actions -->
                <v-col cols="12" class="d-flex justify-start pa-4">
                  <v-btn @click="saveChanges()" class="save-button ml-5" :loading="progressShow"
                    :disabled="progressShow">
                    <v-icon left>mdi-content-save</v-icon>
                    שמור שינויים
                  </v-btn>
                  <v-btn @click="deleteOperation()" class="delete-button" :loading="progressShow"
                    :disabled="progressShow">
                    <v-icon left>mdi-delete</v-icon>
                    מחק פעולה
                  </v-btn>
                </v-col>

                <v-col cols="12" v-if="progressShow" class="d-flex justify-center">
                  <v-progress-circular indeterminate :color="$store.state.colorPrimary"></v-progress-circular>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    <SocketDataDialog v-model="socketDataDialog" v-if="socketDataDialog" :data="socketData"></SocketDataDialog>
  </v-container>
</template>

<script>
import ApiServices from '@/services/api.service.js'
import SnackBar from '@/components/widgets/snackBar.vue'
import SocketDataDialog from '@/components/user/crmIntigration/SocketResponseDialog.vue'
import Auth from '@/services/auth.service.js'
import { io } from 'socket.io-client';

export default {
  components: {
    SnackBar,
    SocketDataDialog
  },
  props: {
    operationToEdit: Object,
    tableName: Object,
    trigers: Array,
    value: { type: Boolean, default: false },
  },
  data: () => ({
    fields: [],
    tables: [],
    progressShow: false,
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    editedOperation: {},
    requestMethod: ["GET", "POST", "PUT", "DELETE"],
    operatorsList: ["===", "!==", "<", ">", "<=", ">="],
    socket: null,
    socketEnabled: false,
    socketData: [],
    socketDataDialog: false,
    showUrlHelp: false,
    showHeadersHelp: false,
    showBodyHelp: false,
    showConditionHelp: false
  }),
  computed: {
    openMode: {
      get() {
        return this.value
      },
      set(v) {
        return this.$emit('input', v)
      }
    },
    jsonRule() {
      return (value) => {
        if (this.editedOperation.action.method !== 'GET') {
          try {
            if (!this.editedOperation.action.headers || !this.editedOperation.action.headers.includes("application/json")) {
              return true
            }
            JSON.parse(value);
            return true; // Valid JSON
          } catch (e) {
            return 'The body must be valid JSON.'; // Invalid JSON
          }
        }
        return true; // If method is GET, pass validation regardless of JSON validity
      };
    }
  },
  methods: {
    async saveChanges() {
      if (this.$refs.form.validate()) {
        try {
          if (this.editedOperation.action.mode === true) {
            this.editedOperation.action.mode = "on"
          } else {
            this.editedOperation.action.mode = "off"
          }
          if (this.editedOperation.action.send_reports === true) {
            this.editedOperation.action.send_reports = "yes"
          } else {
            this.editedOperation.action.send_reports = "no"
          }
          let token = localStorage.getItem("token");
          const tableJSON = JSON.stringify(this.editedOperation);

          let api = process.env.VUE_APP_BASE_URL + "/user/update_crm_operation";
          const res = await fetch(api, ApiServices.requestOptions("PUT", tableJSON, token));
          const jsonObject = await res.json();
          if (res.status >= 400) {
            this.showSnackBar("שגיאה בעדכון הפעולה" + jsonObject.message, "red");
          } else if (res.status === 200) {
            this.$emit("operationUpdated", this.editedOperation, "הפעולה עודכנה בהצלחה!", "green");
          }
        } catch (error) {
          this.showSnackBar("Error update operation: " + error, "red");
        }
      }
    },
    async deleteOperation() {
      try {
        let token = localStorage.getItem("token");
        const tableJSON = JSON.stringify(this.editedOperation);

        let api = process.env.VUE_APP_BASE_URL + "/user/delete_crm_operation";
        const res = await fetch(api, ApiServices.requestOptions("DELETE", tableJSON, token));
        const jsonObject = await res.json();
        if (res.status >= 400) {
          this.showSnackBar("שגיאה במחיקת הפעולה" + jsonObject.message, "red");
        } else if (res.status === 200) {
          this.$emit("operationUpdated", this.editedOperation, "הפעולה נמחקה בהצלחה!", "green");
        }
      } catch (error) {
        this.showSnackBar("Error update operation: " + error, "red");
      }
    },
    // Start the socket connection with parameters
    startSocket() {
      if (this.socket) {
        console.log('Socket already connected');
        return;
      }
      this.socketDataDialog = true;
      this.socket = io(process.env.VUE_APP_BASE_URL, { path: "/socketio" }); // Replace with your server URL

      this.socket.emit('joinRoom', {
        token: localStorage.getItem("token"),
        tableName: this.tableName.name,
        taskId: this.operationToEdit._id,
      });

      // Listen for data from the server
      this.socket.on('recieveMessage', (data) => {
        console.log('Data received from server:', data);
        this.socketData.push(data); // Update the data property
      });

      this.socket.on('connect', () => {
        console.log('Socket connected');
      });

      this.socket.on('disconnect', () => {
        console.log('Socket disconnected');
      });

      this.socket.on('error', (error) => {
        console.error('Socket error:', error);
      });
    },

    // Close the socket connection
    closeSocket() {
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
        this.socketDataDialog = false;
        console.log('Socket connection closed');
      }
    },
    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true
    },
  },
  watch: {
    socketEnabled(newValue) {
      console.log(newValue);
      if (newValue) {
        this.startSocket();
      } else {
        this.closeSocket();
      }
    }
  },
  beforeDestroy() {
    // Ensure socket is closed when the component is destroyed
    if (this.socket) {
      this.socket.disconnect();
    }
  },
  created() {

    let auth = Auth.checkAuth();
    if (!auth) {
      this.$router.replace({ name: "login" })
    }
    // deep clone
    this.editedOperation = JSON.parse(JSON.stringify(this.operationToEdit));

    // Convert old array format to string format if needed
    if (Array.isArray(this.editedOperation.action.condition)) {
      // Convert from old format [value1, operator, value2] to string format
      const [value1, operator, value2] = this.editedOperation.action.condition;
      if (value1 && operator && value2) {
        this.editedOperation.action.condition = `${value1} ${operator} ${value2}`;
      } else {
        this.editedOperation.action.condition = '';
      }
    }

    if (this.editedOperation.action.mode === "on" || this.editedOperation.action.mode === true) {
      this.editedOperation.action.mode = true
    } else {
      this.editedOperation.action.mode = false
    }
    if (this.editedOperation.action.send_reports === "yes" || this.editedOperation.action.send_reports === true) {
      this.editedOperation.action.send_reports = true
    } else {
      this.editedOperation.action.send_reports = false
    }
    this.fields = this.$store.state.fields;
    this.fields.unshift({ hebrew_name: "הכל", name: "הכל" });
    // this.tables = this.$store.state.tables;
  },

}
</script>

<style scoped>
.divider {
  border-top: 0.5px solid #cfcccc;
  margin-top: 10px;
  margin-bottom: 10px;
}

.divider-col {
  border-left: 1px solid #ccc !important;
  padding-left: 10px;
  margin-left: 10px;
}

.operation-settings {
  background: linear-gradient(135deg, #f8fafc 0%, #f0f7ff 100%);
  min-height: 100vh;
  padding-top: 70px;
  padding-bottom: 90px;
}

.settings-card {
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 24px rgba(13, 44, 109, 0.08);
}

.settings-header {
  padding: 1.5rem;
  background: linear-gradient(to bottom, rgba(13, 44, 109, 0.02), transparent);
}

.primary-text {
  background: linear-gradient(135deg, #0d2c6d 0%, #1a4ba5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.back-button {
  background: rgba(13, 44, 109, 0.05);
  transition: all 0.3s ease;
}

.back-button:hover {
  background: rgba(13, 44, 109, 0.1);
  transform: translateX(2px);
}

.back-button .v-icon {
  color: #0d2c6d;
}

/* Custom Input Styling */
.custom-input {
  transition: all 0.3s ease;
}

.custom-input>>>.v-input__slot {
  border: 1px solid rgba(13, 44, 109, 0.1) !important;
  transition: all 0.3s ease;
}

.custom-input>>>.v-input__slot:hover {
  border-color: rgba(13, 44, 109, 0.2) !important;
  background: rgba(13, 44, 109, 0.03);
}

.custom-input.v-input--is-focused>>>.v-input__slot {
  border-color: #0d2c6d !important;
  box-shadow: 0 0 0 1px rgba(13, 44, 109, 0.2) !important;
  background: rgba(13, 44, 109, 0.02);
}

/* Condition Card */
.condition-card {
  border: 1px solid rgba(13, 44, 109, 0.1);
  border-radius: 8px;
  background: rgba(13, 44, 109, 0.02);
  padding: 1rem;
}

.condition-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  color: #0d2c6d;
}

.condition-content {
  background: white;
  border-radius: 6px;
  padding: 0.5rem;
}

.condition-string-input>>>input {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
  font-size: 14px !important;
  font-weight: 400 !important;
}

/* Help Icon and Tooltip */
.help-icon {
  transition: all 0.3s ease;
}

.help-icon:hover {
  transform: scale(1.1);
  opacity: 0.8;
}

.help-tooltip {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  direction: rtl;
  text-align: right;
  max-height: 400px;
  overflow: hidden;
}

.help-title {
  font-size: 16px;
  font-weight: 600;
  color: #0d2c6d;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(13, 44, 109, 0.1);
  padding-bottom: 8px;
  position: relative;
  padding-left: 32px;
}

.help-content {
  max-height: 320px;
  overflow-y: auto;
  padding-left: 4px;
}

.help-content::-webkit-scrollbar {
  width: 6px;
}

.help-content::-webkit-scrollbar-track {
  background: rgba(13, 44, 109, 0.05);
  border-radius: 3px;
}

.help-content::-webkit-scrollbar-thumb {
  background: rgba(13, 44, 109, 0.2);
  border-radius: 3px;
}

.help-content::-webkit-scrollbar-thumb:hover {
  background: rgba(13, 44, 109, 0.3);
}

.help-section {
  margin-bottom: 12px;
}

.help-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #0d2c6d;
  margin-bottom: 8px;
}

.help-example {
  background: rgba(13, 44, 109, 0.05);
  padding: 6px 10px;
  border-radius: 4px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 13px;
  color: #0d2c6d;
  margin-bottom: 6px;
  direction: ltr;
  text-align: left;
  font-weight: 500;
}

.help-description {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
}

.help-variables {
  /* Removed max-height and overflow-y since parent handles scrolling */
}

.help-variable {
  background: rgba(13, 44, 109, 0.03);
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  margin-bottom: 4px;
  border-right: 3px solid rgba(13, 44, 109, 0.2);
}

/* Code Font */
.code-font>>>textarea {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

/* Switches Container */
.switches-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(13, 44, 109, 0.02);
  border-radius: 8px;
}

.switch-item {
  position: relative;
}

/* Buttons */
.save-button {
  background: linear-gradient(135deg, #0d2c6d 0%, #1a4ba5 100%) !important;
  color: white !important;
  padding: 0 1.5rem !important;
  height: 40px;
  text-transform: none;
  letter-spacing: 0.5px;
  font-size: 0.9375rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.save-button:hover:not(:disabled) {
  opacity: 0.95;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(13, 44, 109, 0.2) !important;
}

.delete-button {
  background: rgba(239, 68, 68, 0.1) !important;
  color: #ef4444 !important;
  padding: 0 1.5rem !important;
  height: 40px;
  text-transform: none;
  letter-spacing: 0.5px;
  font-size: 0.9375rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.delete-button:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.15) !important;
  transform: translateY(-1px);
}

/* RTL Support */
.v-application {
  direction: rtl;
}

.v-text-field>>>.v-input__prepend-inner {
  margin-right: 0;
  margin-left: 8px;
}

.v-text-field>>>.v-input__append-inner {
  margin-left: 0;
  margin-right: 8px;
}

.view-icon {
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-icon:hover {
  opacity: 0.8;
  transform: scale(1.1);
}

.close-help {
  position: absolute;
  top: 8px;
  left: 8px;
}
</style>